import { useEffect } from "react";

// Mobile bottom sheet: backdrop click / Escape / close button all dismiss it.
export default function BottomSheet({ open, onClose, title, subtitle, children, footer }) {
    useEffect(() => {
        if (!open) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-end justify-center xl-body">
            <style>{`
                @keyframes sheet-up { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes sheet-fade { from { opacity: 0; } to { opacity: 1; } }
                .sheet-panel { animation: sheet-up 0.3s cubic-bezier(0.22,1,0.36,1) both; }
                .sheet-backdrop { animation: sheet-fade 0.25s ease both; }
                @media (prefers-reduced-motion: reduce) { .sheet-panel, .sheet-backdrop { animation: none !important; } }
            `}</style>

            <div
                className="sheet-backdrop absolute inset-0 bg-[#08213D]/55"
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="sheet-panel relative w-full max-w-[430px] md:max-w-[680px] lg:max-w-[860px] max-h-[88vh] flex flex-col rounded-t-[28px] bg-[#F4FAFC] shadow-[0_-20px_60px_rgba(4,33,63,0.3)]"
            >
                <div className="flex justify-center pt-2.5">
                    <span className="w-10 h-1 rounded-full bg-[#C9DCE5]" />
                </div>

                <div className="flex items-start justify-between gap-3 px-5 pt-3 pb-3 border-b border-[#DCEAF1]">
                    <div className="min-w-0">
                        <h2 className="xl-display text-[18px] font-bold text-[#08213D] leading-tight truncate">
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="text-[12px] font-medium text-[#5B6B7C] mt-0.5 truncate">{subtitle}</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 w-9 h-9 rounded-full bg-white border border-[#DCEAF1] text-[#5B6B7C] flex items-center justify-center active:scale-95 transition"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

                {footer && (
                    <div className="px-5 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] border-t border-[#DCEAF1] bg-white/90 backdrop-blur-md">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}